import React from 'react';
import queryString from 'query-string';
import Styled from 'styled';
import { RouteComponentProps } from 'react-router';
import Helmet from 'helmet/helmet';
import debounce from 'lodash/debounce';
import { Query } from '@apollo/client/react/components';
import PropTypes from 'prop-types';
import { Site } from 'four-nets/site';
import { FormattedMessage } from 'react-intl';
import { mapAfter } from 'apollo-relay/update-query';
import classnames from 'classnames';

import LoadingProgress from 'lib/react-router/loading-progress';
import ScrollToTop from 'lib/scroll/scroll-to-top';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import ErrorPage from 'four-nets/errors/error-page';
import LeftMenu from 'four-nets/photoblog/left-menu/left-menu';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';
import PopularImages from 'four-nets/photoblog/popular-images/popular-images';
import { MediaKey } from 'four-nets/photoblog/masonry/masonry';

import CollectionNode from './collection-node/collection-node';
import collectionsQuery, { CollectionsLoaderQuery, CollectionsLoaderQueryVariables } from './collections.graphql';

import searchInputStyles from './search-input-styles.scss';
import defaultStyles from './collections.scss';
import InfiniteScroll from 'lib/infinite-scroll/infinite-scroll';

const columnsSizes: Map<MediaKey, number> = new Map([
    ['ms', 2],
    ['lg', 3],
]);

interface Search {
    offset?: number;
    search?: string;
    filterMy?: boolean; // TODO: enum from graphql
}

interface Params {
    tab?: string;
}

enum Tab {
    ALL = 'all',
    MINE = 'mine',
    FAVOURITE = 'favourite',
}

export interface Props extends RouteComponentProps<Params> {
    styles: typeof defaultStyles;
    search: Search;
}
interface State {
    search: string;
    autoLoadActive: boolean;
}

const FIRST = 26;

class Collections extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    searchGroupWithDebounce = debounce((search: string) => {
        this.props.history.push({
            pathname: this.props.location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(this.props.location.search),
                search,
            })}`,
        });
    }, 2000);
    fetchMore?: () => Promise<any> = undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            search: props.search.search ? props.search.search : '',
            autoLoadActive: false,
        };
    }

    onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ search: event.target.value }, () => {
            this.searchGroupWithDebounce(this.state.search);
        });
    };

    changeTabWith = (tab: string) => {
        this.setState({ search: '' }, () => {
            const pathname = `/blogs/collections${tab !== Tab.ALL ? `/${tab}/` : '/'}`;
            this.props.history.push({ pathname });
        });
    };

    renderHelmet = (H: any) => {
        const { site } = this.context;
        const {
            match: {
                params: { tab = Tab.ALL },
            },
        } = this.props;

        return tab === Tab.ALL ? (
            <FormattedMessage id='PB_MENU_COLLECTION_LIST'>
                {message => (
                    <H>
                        <title>
                            {message} | {site.name}
                        </title>
                    </H>
                )}
            </FormattedMessage>
        ) : tab === Tab.FAVOURITE ? (
            <FormattedMessage id='PB_MENU_COLLECTION_FAVORITE'>
                {message => (
                    <H>
                        <title>
                            {message} | {site.name}
                        </title>
                    </H>
                )}
            </FormattedMessage>
        ) : (
            <FormattedMessage id='PB_MENU_COLLECTION_MINE'>
                {message => (
                    <H>
                        <title>
                            {message} | {site.name}
                        </title>
                    </H>
                )}
            </FormattedMessage>
        );
    };

    onClick = () => {
        if (this.fetchMore) {
            this.fetchMore();
            if (!this.state.autoLoadActive) {
                this.setState({ autoLoadActive: true });
            }
        }
    };

    render() {
        const {
            styles,
            match: {
                params: { tab = Tab.ALL },
            },
            location: { pathname },
            search: { offset },
        } = this.props;

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${parseInt(offset as any, 10) - 1}`);
        }

        return (
            <div className={styles.columns}>
                <Helmet render={this.renderHelmet} />
                <ScrollToTop />
                <div className={styles.left}>
                    <h1 className={styles.title}>
                        <FormattedMessage id='COLLECTIONS' />
                    </h1>
                    <LeftMenu />
                </div>
                <div className={styles.right}>
                    <div className={styles.nav}>
                        <div className={styles.tabBar}>
                            <button
                                className={classnames(styles.tab, { [styles.tabHighligted]: tab === Tab.ALL })}
                                onClick={() => {
                                    this.changeTabWith(Tab.ALL);
                                }}
                            >
                                <FormattedMessage id='COLLECTION_TABS_ALL' />
                            </button>
                            <button
                                className={classnames(styles.tab, { [styles.tabHighligted]: tab === Tab.MINE })}
                                onClick={() => {
                                    this.changeTabWith(Tab.MINE);
                                }}
                            >
                                <FormattedMessage id='COLLECTION_TABS_MINE' />
                            </button>
                            <button
                                className={classnames(styles.tab, { [styles.tabHighligted]: tab === Tab.FAVOURITE })}
                                onClick={() => {
                                    this.changeTabWith(Tab.FAVOURITE);
                                }}
                            >
                                <FormattedMessage id='COLLECTION_TABS_FAVOURITE' />
                            </button>
                        </div>
                    </div>
                    <ViewerConsumer>
                        {({ viewer }) =>
                            viewer || tab === Tab.ALL ? (
                                <Query<CollectionsLoaderQuery, CollectionsLoaderQueryVariables>
                                    query={collectionsQuery}
                                    notifyOnNetworkStatusChange={true}
                                    variables={{
                                        first: FIRST,
                                        myCollections: tab === Tab.MINE,
                                        favourite: tab === Tab.FAVOURITE,
                                        title: this.state.search,
                                        minImageCount: tab === Tab.ALL ? 3 : null,
                                        after,
                                        //Cache busters
                                        isAuth: Boolean(viewer),
                                        ...(tab === Tab.MINE && viewer && { username: viewer.username }),
                                    }}
                                    fetchPolicy='cache-first'
                                >
                                    {({ loading, data, error, fetchMore }) => {
                                        const endCursor =
                                            data && data.collections && data.collections.pageInfo.endCursor;

                                        const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
                                            endCursor,
                                            pathname,
                                            pageSize: FIRST,
                                        });

                                        let nextOffset;
                                        if (endCursor) {
                                            nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
                                        } else {
                                            nextOffset = offset;
                                        }

                                        this.fetchMore = () =>
                                            fetchMore({
                                                variables: {
                                                    after: endCursor,
                                                    first: FIRST,
                                                },
                                                updateQuery: (
                                                    previousResult = {} as CollectionsLoaderQuery,
                                                    { fetchMoreResult = {} as CollectionsLoaderQuery }
                                                ) => {
                                                    return {
                                                        collections: mapAfter(
                                                            previousResult.collections,
                                                            fetchMoreResult.collections
                                                        ),
                                                    };
                                                },
                                            });

                                        return (
                                            <LoadingProgress loading={loading}>
                                                <ErrorPage error={error}>
                                                    {data && data.collections ? (
                                                        <div className={styles.content}>
                                                            <ul className={styles.collections}>
                                                                {data.collections.edges &&
                                                                data.collections.edges.length ? (
                                                                    data.collections.edges.map((edge, index) => {
                                                                        return [
                                                                            edge && edge.node ? (
                                                                                <li
                                                                                    key={index}
                                                                                    className={styles.collectionItem}
                                                                                >
                                                                                    <CollectionNode {...edge.node} />
                                                                                </li>
                                                                            ) : null,
                                                                            tab === Tab.ALL &&
                                                                            (index === 6 ||
                                                                                (index ===
                                                                                    data!.collections!.edges.length -
                                                                                        1 &&
                                                                                    index < 6)) ? (
                                                                                <li
                                                                                    className={styles.popularImages}
                                                                                    key={-1}
                                                                                >
                                                                                    <PopularImages
                                                                                        pathname={pathname}
                                                                                        columnsSizes={columnsSizes}
                                                                                        isPartOfOtherPage={true}
                                                                                    />
                                                                                </li>
                                                                            ) : null,
                                                                        ];
                                                                    })
                                                                ) : loading ? null : (
                                                                    <div className={styles.noCollections}>
                                                                        <FormattedMessage id='NO_COLLECTIONS' />
                                                                    </div>
                                                                )}
                                                            </ul>
                                                            <InfiniteScroll
                                                                fetch={this.fetchMore}
                                                                disabled={!this.state.autoLoadActive}
                                                            >
                                                                {data.collections.pageInfo &&
                                                                data.collections.pageInfo.hasNextPage ? (
                                                                    <FetchMoreButton
                                                                        disabled={loading}
                                                                        href={endCursorHref}
                                                                        prevHref={prevCursorHref}
                                                                        loading={loading}
                                                                        onClick={this.onClick}
                                                                    />
                                                                ) : null}
                                                            </InfiniteScroll>
                                                            {(data.collections.pageInfo &&
                                                                data.collections.pageInfo.hasNextPage) ||
                                                            offset ? (
                                                                <OffsetPager
                                                                    style={{ nav: styles.navOfOffsetPager }}
                                                                    pageSize={FIRST}
                                                                    offset={nextOffset}
                                                                />
                                                            ) : null}
                                                        </div>
                                                    ) : null}
                                                </ErrorPage>
                                            </LoadingProgress>
                                        );
                                    }}
                                </Query>
                            ) : (
                                <Unauthorized />
                            )
                        }
                    </ViewerConsumer>
                </div>
            </div>
        );
    }
}

export default Styled(defaultStyles)(Collections);
